import CachedImg from '../CachedImg';
import { colorDeCarpeta } from '../../pages/v2/moodboardsApi';

/**
 * El ícono de una carpeta de moodboard — ADR-2026-09-13-moodboards-carpetas-de-referencia,
 * con dos variantes y color desde ADR-2026-09-13-moodboards-color-icono-cerrado-y-solo-escritorio.
 *
 * Dos estados, y los decide el CONTENIDO, no quien lo monta:
 *
 * - **Cerrada** (sin fotos): el frente sube casi hasta la pestaña y la carpeta se lee
 *   tapada. Es lo que se ve al crearla, y dice "acá adentro todavía no hay nada" sin
 *   una sola palabra.
 * - **Abierta** (con fotos): el frente baja y las últimas fotos asoman por encima,
 *   como papeles adentro de una carpeta real.
 *
 * Está armado con divs y no con un `<svg>` de un solo path porque las fotos van ENTRE
 * dos capas de la carpeta: en SVG habría que meterlas en un `foreignObject` (que no
 * recorta igual en todos los navegadores) o pasarlas a `<image>` con `clipPath`, y ahí
 * se pierde `CachedImg`, que es lo que evita el ícono de imagen rota mientras la firma
 * carga (ADR-015).
 *
 * Todas las medidas son porcentuales: el ícono se dibuja al tamaño que le dé el
 * contenedor.
 */

/** Dónde empieza el frente en cada variante, como % de la altura del ícono. */
const FRENTE_CERRADA = '30%';
const FRENTE_ABIERTA = '48%';

export const IconoCarpeta = ({
  portada,
  color,
  className = '',
}: {
  /** Miniaturas ya firmadas (hasta 3). Vacío = carpeta cerrada. */
  portada: string[];
  /** ID del color elegido; `null` o desconocido caen al default. */
  color?: string | null;
  className?: string;
}) => {
  const cerrada = portada.length === 0;
  const { fondo, frente } = colorDeCarpeta(color);

  return (
    <div className={`relative w-full aspect-[4/3] ${className}`}>
      {/* Pestaña. `rounded-tr-[10px]` además de la izquierda: en iOS la pestaña sube
          con una curva de este lado, no en escuadra. */}
      <div
        className="absolute left-0 top-0 h-[22%] w-[46%] rounded-tl-[8px] rounded-tr-[10px]"
        style={{ backgroundColor: fondo }}
      />
      {/* Cuerpo de atrás. Arranca donde termina la pestaña y se solapa unos puntos con
          ella para que no quede una costura clara entre las dos. */}
      <div
        className="absolute inset-x-0 top-[17%] bottom-0 rounded-[12px] rounded-tl-[2px]"
        style={{ backgroundColor: fondo }}
      />

      {/* Las fotos, entre el fondo y el frente. Sólo en la variante abierta: que no
          haya ninguna es JUSTO lo que define a la cerrada. */}
      {!cerrada && (
        <div className="absolute inset-x-[9%] top-[27%] flex justify-center gap-[4%]">
          {portada.map((url, i) => (
            <div
              key={`${i}-${url}`}
              className="w-[27%] aspect-[3/4] overflow-hidden rounded-[4px] bg-white shadow-[0_1px_3px_rgb(var(--brand-black-rgb)_/_0.18)]"
              // Las de los costados un pelo más abajo: el escalonado es lo que las
              // hace leer como varias hojas y no como una tira pegada.
              style={{ marginTop: i === 1 ? 0 : '4%' }}
            >
              <CachedImg src={url} alt="" className="h-full w-full object-cover" />
            </div>
          ))}
        </div>
      )}

      {/* Frente. Va último para quedar por encima de las fotos, y lleva una línea clara
          arriba que hace de canto del cartón. Su ALTURA es la diferencia entre las dos
          variantes: alto tapa la carpeta, bajo la deja mostrar lo que tiene. */}
      <div
        className="absolute inset-x-0 bottom-0 rounded-[12px] border-t border-white/45 transition-[top] duration-200"
        style={{
          top: cerrada ? FRENTE_CERRADA : FRENTE_ABIERTA,
          backgroundColor: frente,
        }}
      />
    </div>
  );
};

export default IconoCarpeta;
